// apps/dashboard/src/hooks/use-branding.ts
// Fetches the tenant's white-label brandingConfig from GET /api/me/branding.
// Cached for 5 minutes — only fetched when the user is authenticated.

import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface BrandingConfig {
  logoUrl?: string;
  /** CSS hex colour, e.g. "#2563EB" or "#7C3AED". Applied as CSS --primary variable. */
  primaryColor?: string;
  customDomain?: string;
  removeOmniflowBranding?: boolean;
}

interface BrandingResponse {
  brandingConfig: BrandingConfig;
}

export function useBranding() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery({
    queryKey: ['branding'],
    queryFn: async () => {
      // First try to resolve branding based on host (works on login page)
      // We use branding/current because the Gateway routes it as /api/branding/current
      try {
        const publicBranding = await api.get<BrandingConfig>('/branding/current');
        if (publicBranding && !('error' in publicBranding)) {
          return { brandingConfig: publicBranding };
        }
      } catch (_err) {
        // Silent fail for public branding, it's expected if no custom domain or not configured
      }

      // Fallback to authenticated /me/branding if public fails or user is logged in
      if (isAuthenticated) {
        try {
          const res = await api.get<BrandingResponse>('/me/branding');
          return res;
        } catch (innerErr) {
          console.error('Failed to fetch authenticated branding:', innerErr);
        }
      }

      // If both fail and not authenticated, return default branding instead of throwing
      // to avoid breaking the UI layout/rendering.
      return {
        brandingConfig: {
          primaryColor: '#2563EB',
          removeOmniflowBranding: false,
        },
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1, // Allow one retry for network/startup glitches
  });
}

export function usePatchBranding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      patch: Partial<BrandingConfig> & {
        logoUrl?: string | null;
        primaryColor?: string | null;
        customDomain?: string | null;
      },
    ) => api.patch<BrandingResponse>('/me/branding', patch),
    onSuccess: (data) => {
      // Update the cache immediately so BrandingProvider re-renders
      queryClient.setQueryData(['branding'], data);
    },
  });
}

export function useBrandSettings() {
  return useBranding();
}
